import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { StorageService } from 'src/app/core/services/storage.service';
import { AuthService, sanitizeAppPathSegmentForRedirect } from 'src/app/core/services/auth.service';
import { RouteService } from 'src/app/core/services/route.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { take, filter } from 'rxjs/operators';
import { createAuth0Client, Auth0Client } from '@auth0/auth0-spa-js';
import { AlertService, ToastTypes } from 'src/app/shared/services/alert.service';
import { environment } from 'src/environments/environment';
import { OGAuth0Service } from 'src/app/core/services/og-auth0.service';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { ApplicationService } from 'src/app/core/state/_services/application.service';

export const Auth0CallbackFactory = {
  create: (config: any) => createAuth0Client(config),
};

@Component({
  selector: 'app-callback',
  template: `<div>Processing login...</div>`
})
export class CallbackComponent implements OnInit {
  private otherAuth0Client: Auth0Client | null = null;

  constructor(
    private readonly storage: StorageService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: RouteService,
    private readonly auth0Service: Auth0Service,
    private readonly alert: AlertService,
    private readonly loadingService: LoadingService,
    private readonly ogAuth0Service: OGAuth0Service,
    private readonly ldService: LaunchDarklyService,
    private readonly applicationService: ApplicationService
  ) {
  }

  async ngOnInit() {
    
    try {
      // Check if this is an external login (Other Auth0)
      const isExternalLogin = this.storage.getIsExternalLogin();
      
      if (isExternalLogin) {
        // Handle the "Other Auth0" flow
        await this.handleOtherAuth0Callback();
      } else {
        // Handle the main Auth0 SDK flow
        await this.handleMainAuth0Callback();
      }
    } catch (error) {
      console.error('Error in ngOnInit:', error);
    }
    
  }

  // Extract URL checking methods for better testability
  protected getCurrentUrlSearch(): string {
    return window.location.search;
  }

  protected getCurrentUrlHref(): string {
    return window.location.href;
  }

  protected getCurrentUrlOrigin(): string {
    return window.location.origin;
  }

  protected hasAuthorizationCode(): boolean {
    return this.getCurrentUrlSearch().includes('code=');
  }

  // Avoid cross-origin errors in tests when replacing URL
  protected safelyReplaceUrl(path: string): void {
    try {
      window.history.replaceState({}, document.title, path);
    } catch {
      // ignore in non-browser/test environments
    }
  }

  private async redirectAfterLogin(appId?: any): Promise<void> {
    // Clear loading state before redirecting to ensure loader doesn't persist
    this.loadingService.loadingState = false;
    this.loadingService.tableLoadingState = false;

    const redirectTo = this.route.getParam?.('redirectTo');
    if (redirectTo && redirectTo !== '/auth/callback') {
      this.route.navigate([redirectTo], { replaceUrl: true });
      // Probe single-app status so App Library hides for single-app users
      // even when redirected back to a stored URL rather than going through the normal flow.
      this.applicationService.resolveSingleAppPostLogin$().pipe(take(1)).subscribe();
      return;
    }
    if (appId) {
      const safeAppId = sanitizeAppPathSegmentForRedirect(appId);
      if (safeAppId) {
        this.route.navigate(['/apps', safeAppId], { replaceUrl: true });
      } else {
        this.route.navigate(['/apps'], { replaceUrl: true });
      }
      // Probe single-app status so App Library hides for single-app users
      // even when the login URL included an appId that bypasses resolveSingleAppPostLogin$.
      this.applicationService.resolveSingleAppPostLogin$().pipe(take(1)).subscribe();
      return;
    }
    const resolution = await this.applicationService
      .resolveSingleAppPostLogin$()
      .pipe(take(1))
      .toPromise();
    if (
      resolution &&
      typeof resolution === 'object' &&
      'appKey' in resolution
    ) {
      const safeAppKey = sanitizeAppPathSegmentForRedirect(
        (resolution as { appKey: unknown }).appKey
      );
      if (safeAppKey) {
        this.route.navigate(['/apps', safeAppKey], { replaceUrl: true });
      } else {
        this.route.navigate(['/apps'], { replaceUrl: true });
      }
    } else {
      this.route.navigate(['/apps'], { replaceUrl: true });
    }
  }

  private async handleOtherAuth0Callback() {
    try {
      
      // Prevent duplicate processing across HMR or double inits
      const callbackAlreadyHandled = sessionStorage.getItem('otherAuth0Handled') === 'true';
      const callbackHandlingInProgress = sessionStorage.getItem('otherAuth0Handling') === 'true';
      if (callbackAlreadyHandled && this.hasAuthorizationCode()) {
        this.safelyReplaceUrl(this.getCurrentUrlOrigin() + '/callback-handler');
        return;
      }
      if (callbackHandlingInProgress && this.hasAuthorizationCode()) {
        return;
      }

      // Do not clear Auth0 SPA transaction storage here, it is needed for handleRedirectCallback
      
      // Get the shared OG Auth0 client (uses refresh tokens)
      this.otherAuth0Client = await this.ogAuth0Service.getClient();

      // Handle the redirect callback
      if (this.hasAuthorizationCode()) {
        
        // Handle the redirect callback; pass the full URL explicitly
        sessionStorage.setItem('otherAuth0Handling', 'true');
        await this.otherAuth0Client.handleRedirectCallback(this.getCurrentUrlHref());
        // Mark as handled and clean URL to prevent duplicate processing
        sessionStorage.setItem('otherAuth0Handled', 'true');
        sessionStorage.removeItem('otherAuth0Handling');
        this.safelyReplaceUrl(this.getCurrentUrlOrigin() + '/callback-handler');
        
        // Check if authenticated
        const isAuthenticated = await this.otherAuth0Client.isAuthenticated();
        if (!isAuthenticated) {
          throw new Error('Other Auth0 authentication failed');
        }

        // Get user and token info
        const userData = await this.otherAuth0Client.getUser();
        const token = await this.otherAuth0Client.getTokenSilently();
        

        // Store tokens using your app's method (fetch config first for encryption)
        await this.authService.fetchConfigAndSetTokens({
          access_token: token,
          id_token: token, // Use access token as ID token for compatibility
          token_type: 'Bearer',
          expires_in: 7200
        });
        
        // Debug: Check if tokens are properly stored

        // Check if user exists in your backend; treat 401 as non-fatal in external flow
        const appId = this.route.getApplicationKey?.() || this.storage.getPortalId?.();
        
        let isUserExist = false;
        try {
          isUserExist = await this.authService.checkIfUserExist({
            ...userData,
            ApplicationKey: appId,
          });
        } catch (e: any) {
          if (e?.status === 401) {
            try {
              this.alert.toast(
                ToastTypes.ERROR,
                'Unauthorized',
                e?.error?.Message || 'You are not authorized to access this application.'
              );
            } catch {}
            this.authService.logout(false, true, appId || null, true);
            return;
          }
          isUserExist = true;
        }

        if (isUserExist) {
          this.authService.isLoggedIn = true;
          this.storage.clearRedirectURL?.();
          await this.redirectAfterLogin(appId);
        }
      } else {
        // If we've already handled the callback and URL was cleaned, do not error
        if (callbackAlreadyHandled) {
          const appId = this.route.getApplicationKey?.() || this.storage.getPortalId?.();
          await this.redirectAfterLogin(appId);
          return;
        }
        throw new Error('No authorization code found in callback');
      }
    } catch (error) {
      console.error('Error handling Other Auth0 callback:', error);
      sessionStorage.removeItem('otherAuth0Handling');
      // Ensure artifacts are cleared and redirect to app-specific login when possible
      try {
        const appId = this.storage.getPortalId?.() || this.route.getApplicationKey?.() || null;
        // Don't include redirect param when failing from callback
        this.authService.logout(false, true, appId, true);
      } catch {
        this.router.navigate(['/auth/login']);
      }
    }
  }

  private async handleMainAuth0Callback() {
    try {
      
      // Explicitly process the Auth0 redirect callback to prevent default navigation
      try {
        await this.auth0Service.handleRedirectCallback().pipe(take(1)).toPromise();
      } catch (callbackErr) {
      }
      
      // Wait for Auth0 to finish processing the callback
      await this.auth0Service.isLoading$.pipe(
        filter(isLoading => !isLoading),
        take(1)
      ).toPromise();
      
      // Check if authentication was successful
      const isAuthenticated = await this.auth0Service.isAuthenticated$.pipe(take(1)).toPromise();
      
      if (!isAuthenticated) {
        throw new Error('Authentication failed');
      }

      // Get user and token info
      const userData = await this.auth0Service.user$.pipe(take(1)).toPromise();
      
      // Try to get the access token without detailed response first
      let token;
      try {
        token = await this.auth0Service.getAccessTokenSilently({ detailedResponse: true }).pipe(take(1)).toPromise();
      } catch (tokenError) {
        // Fallback to simple token if detailed response fails
        const accessToken = await this.auth0Service.getAccessTokenSilently().pipe(take(1)).toPromise();
        token = {
          access_token: accessToken,
          token_type: 'Bearer',
          expires_in: 86400 // Default to 24 hours
        };
      }

      if (!userData || !token) {
        throw new Error('Failed to get user data or token');
      }


      // Store tokens using your app's method (fetch config first for encryption)
      await this.authService.fetchConfigAndSetTokens(token);
      const isRefreshEnabled = await this.ldService.getFlagValue(FEATURE_FLAGS.ENABLE_REFRESH_TOKEN, false).pipe(take(1)).toPromise();
      if (isRefreshEnabled) {
        this.storage.setAuthProvider('auth0');
      }

      // Check if user exists in your backend; treat 401 as non-fatal in external flow
      const appId = this.route.getApplicationKey?.() || this.storage.getPortalId?.();
      
      let isUserExist = false;
      try {
        isUserExist = await this.authService.checkIfUserExist({
        ...userData,
        ApplicationKey: appId,
      });
      } catch (e) {
        // For external SSO users, 401 errors are common and expected
        isUserExist = true;
      }

      // For external SSO users, we should proceed even if the backend check fails
      if (isUserExist) {
        this.authService.isLoggedIn = true;
        this.storage.clearRedirectURL?.();
        await this.redirectAfterLogin(appId);
      }
    } catch (error) {
      console.error('Error handling main Auth0 callback:', error);
      // Redirect back to login on error
      this.router.navigate(['/auth/login']);
    }
  }
} 